import React, { useEffect } from 'react'

import { useForm } from 'react-hook-form'
import {
  Button,
  FormControl,
  FormErrorMessage,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Textarea,
} from '@chakra-ui/react'

import {
  useCandidate,
  useCreateCandidate,
  useUpdateCandidate,
} from '@/react-query/candidate'
import { useInterview } from '@/react-query/interview/interview'

interface EditCVForm {
  fullName: string
  cvText: string
  cvFileName: string
}

export const EditCVModal = ({
  isOpen,
  onClose,
}: {
  isOpen: boolean
  onClose: () => void
}) => {
  const { data: candidate } = useCandidate()
  const updateCandidate = useUpdateCandidate()
  const createCandidate = useCreateCandidate()

  const defaultValues: EditCVForm = React.useMemo(
    () => ({
      fullName: candidate?.fullName || '',
      cvText: candidate?.cvText || '',
      cvFileName: candidate?.cvFileName || '',
    }),
    [JSON.stringify(candidate)],
  )

  const {
    handleSubmit,
    formState: { errors },
    register,
    reset,
  } = useForm<EditCVForm>({
    defaultValues,
  })

  useEffect(() => {
    reset(defaultValues)
  }, [JSON.stringify(candidate)])

  const handleEditCVFormSubmit = async ({
    fullName,
    cvText,
    cvFileName,
  }: EditCVForm) => {
    if (!candidate) {
      await createCandidate.mutateAsync({
        candidate: {
          fullName,
          cvText,
          cvFileName,
        },
      })
    } else {
      await updateCandidate.mutateAsync({
        candidate: {
          id: candidate.id,
          fullName,
          cvText,
          cvFileName,
        },
      })
    }
    onClose()
  }

  const { data: interview } = useInterview()
  const interviewType = interview?.type

  const candidateWord =
    interviewType === 'INTAKE' ? 'hiring manager' : 'candidate'

  return (
    <Modal size={'3xl'} isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <form onSubmit={handleSubmit(handleEditCVFormSubmit)}>
          <ModalHeader>
            {candidate ? `Edit ${candidateWord}` : `New ${candidateWord}`}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody display={'flex'} flexDirection={'column'}>
            <FormControl isInvalid={Boolean(errors.fullName)}>
              <Input
                id="fullName"
                w={'xs'}
                rounded={'1.25rem'}
                placeholder="Full name"
                {...register('fullName', {
                  required: 'Full Name is required',
                })}
                variant={'brand-solid-white'}
              />
              <FormErrorMessage>
                {errors.fullName && errors.fullName.message}
              </FormErrorMessage>
            </FormControl>

            <Textarea
              id="cvText"
              mt={5}
              height={'16rem'}
              resize="none"
              rounded={'1.25rem'}
              placeholder={
                interviewType === 'INTAKE'
                  ? 'Insert any useful information about the hiring manager here'
                  : 'Copy the text of a full CV and insert here'
              }
              {...register('cvText')}
              variant={'brand-solid-white'}
            />
          </ModalBody>

          <ModalFooter>
            <Button
              type="submit"
              variant={'brand-solid'}
              rounded={'1.25rem'}
              isLoading={createCandidate.isPending}
            >
              {candidate ? 'Update' : 'Create'}
            </Button>

            <Button
              rounded={'1.25rem'}
              variant="brand-solid-white"
              onClick={onClose}
            >
              Cancel
            </Button>
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  )
}
